import { useId } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface ArchiveSealProps {
  picture?: string;
  name?: string;
  loading?: boolean;
  /** Size of the whole seal: pixels, or any CSS length (e.g. `min(80vw, 50svh)`). Default 144. */
  size?: number | string;
  /** Outline thickness in px. Default 3; use less for small renderings. */
  borderWidth?: number;
  /** Optional microprint following the lower edge; only used on the large hero seal. */
  imprint?: string;
  className?: string;
}

/**
 * The archive's seal. The npub's profile picture IS the seal artwork, so this
 * simply fills the entire circle with it and frames it in a solid black
 * outline. Falls back to a monogram if no picture is set.
 */
export function ArchiveSeal({
  picture,
  name,
  loading = false,
  size = 144,
  borderWidth = 3,
  imprint,
  className,
}: ArchiveSealProps) {
  const imprintPathId = useId();

  if (loading) {
    return (
      <Skeleton
        className={`rounded-full ${className ?? ''}`}
        style={{ width: size, height: size }}
      />
    );
  }

  const initial = (name ?? '?')[0]?.toUpperCase() ?? '?';

  return (
    <div
      className={cn('relative', className)}
      style={{ width: size, height: size }}
    >
      <div
        className="h-full w-full overflow-hidden rounded-full border-solid border-foreground bg-muted"
        style={{ borderWidth }}
      >
        {picture ? (
          <img
            src={picture}
            alt={name ?? 'Archive seal'}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-masthead text-5xl text-foreground/70">
            {initial}
          </div>
        )}
      </div>
      {imprint && (
        <>
          {/* The path is invisible: only the lettering sits just outside the
              artwork, like a stamp's serial number, without adding a ring. */}
          <svg
            viewBox="0 0 400 400"
            aria-hidden="true"
            focusable="false"
            className="pointer-events-none absolute inset-0 h-full w-full overflow-visible text-foreground/70"
          >
            <defs>
              <path id={imprintPathId} d="M -10 200 A 210 210 0 0 0 410 200" />
            </defs>
            <text fill="currentColor" fontSize="9" letterSpacing="1.25" className="font-mono">
              <textPath href={`#${imprintPathId}`} startOffset="50%" textAnchor="middle">
                {imprint}
              </textPath>
            </text>
          </svg>
          <span className="sr-only">{imprint}</span>
        </>
      )}
    </div>
  );
}
